import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import type { DraftCompensation } from 'modules/employees/pages/BulkFixContractsPage/types'

import mainDetailsStep from './MainDetailsStep'
import scheduleStep from './ScheduleStep'
import scopingStep from './ScopingStep'
import { computeInitialState, isValidTemplate, wizardReducer } from './state'
import {
  CompensationWizardData,
  CompensationWizardMeta,
  CompletedCompensationWizardData,
  NewCompensationStep,
} from './types'

interface Props {
  meta: CompensationWizardMeta
  initialData: DraftCompensation
  onClose: () => void
  onSubmit: (data: CompletedCompensationWizardData) => void
}

const BulkFixWizard: React.FC<Props> = ({ meta, initialData, onClose, onSubmit }) => {
  const initialWizardData = useLazyRef<CompensationWizardData>(() =>
    computeInitialState(meta, initialData)
  )

  const [wizardData, dispatch] = React.useReducer(wizardReducer, initialWizardData.current)
  const [currentStep, setCurrentStep] = React.useState(0)

  const steps = useLazyRef<NewCompensationStep[]>(() => {
    const draftSteps: NewCompensationStep[] = [mainDetailsStep, scheduleStep]

    // If there is no default legal entity/payroll policy, specify them
    if (
      wizardData.legalEntityId === undefined ||
      // Company may not use payroll, so there may not be policies
      (wizardData.allowedPayrollPolicies.length && wizardData.policyId === undefined)
    ) {
      draftSteps.unshift(scopingStep)
    }

    return draftSteps
  })

  const stepCount = steps.current.length

  const { Component, isCompleted, title } = useUnsafeMemo(() => {
    const step = steps.current[currentStep]
    if (!step) {
      throw new Error(`Step ${currentStep} not found; only ${stepCount} steps available`)
    }
    return step()
  }, [steps, stepCount, currentStep])

  return (
    <Modal
      illustration="money-bag"
      title={title}
      progress={{
        amount: currentStep + 1,
        total: stepCount,
      }}
      onClose={onClose}
      locked
      footer={
        <Box gap="s12" flexDirection="row">
          <Button onClick={onClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>

          {currentStep !== stepCount - 1 ? (
            <Button
              type="primary"
              submit
              disabled={!isCompleted(wizardData)}
              onClick={() => setCurrentStep((prev) => prev + 1)}
            >
              {i18n.t('common.next')}
            </Button>
          ) : (
            <Button
              type="primary"
              submit
              disabled={!isValidTemplate(wizardData)}
              onClick={() => {
                // Used for type checking, if called should already be checked.
                if (isValidTemplate(wizardData)) {
                  onSubmit(wizardData)
                }
              }}
            >
              {i18n.t('common.create')}
            </Button>
          )}
        </Box>
      }
    >
      <Component data={wizardData} onUpdate={dispatch} />
      {/* TODO: prompt the user before exiting like in the parent wizard */}
    </Modal>
  )
}

export default BulkFixWizard
